import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import moment from "moment";
import { db } from "../../firebase";
import { normalizeStatus } from "../../helper/statusMeta";
import { inspectorIds, preQIsNo } from "../../helper/inspections";
import { getUserLabel } from "../../helper/userDisplay";
import { hasOpenQuestion } from "../../helper/propertyTasks";

/**
 * Reads the real data behind the dashboard straight from Firestore
 * (`users` + `properties` + `inspections`). KPIs reflect real inspection
 * history: the status breakdown aggregates `inspections` by status, while
 * `totalProperties` counts the `properties` collection. Degrades gracefully:
 * any failure leaves zeroed stats and flips `error`, so the dashboard never
 * renders broken.
 */
const EMPTY = {
  totalUsers: 0,
  totalProperties: 0,
  totalInspections: 0,
  openInspections: 0,
  completionRate: 0,
  statusBreakdown: {
    pending: 0,
    "in progress": 0,
    completed: 0,
    missed: 0,
    cancelled: 0,
  },
  recentInspections: [],
  inspectorPerformance: [],
  flaggedIssues: { totalFlagged: 0, inspectionsWithIssues: 0, byProperty: [] },
  weeklyTrend: [],
  openQuestions: 0,
};

const useDashboardData = () => {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const [usersSnap, propertiesSnap, inspectionsSnap, tasksSnap] =
          await Promise.all([
            getDocs(collection(db, "users")),
            getDocs(collection(db, "properties")),
            getDocs(collection(db, "inspections")),
            getDocs(collection(db, "property_tasks")),
          ]);

        // Open questions = non-done tasks awaiting a reply. The `hasOpenQuestion`
        // flag is maintained server-side by notifyOnTaskNote.
        let openQuestions = 0;
        tasksSnap.docs.forEach((d) => {
          if (hasOpenQuestion(d.data() || {})) openQuestions += 1;
        });

        const inspections = inspectionsSnap.docs.map((d) => ({
          _id: d.id,
          ...d.data(),
        }));

        // uid -> human label (name, else email, else the raw uid as a last resort)
        const userLabel = {};
        usersSnap.docs.forEach((d) => {
          userLabel[d.id] = getUserLabel({ _id: d.id, ...(d.data() || {}) });
        });

        // All five normalizeStatus outputs need buckets — a missing key would
        // make `undefined += 1` = NaN (missed/cancelled used to hit this).
        const breakdown = {
          pending: 0,
          "in progress": 0,
          completed: 0,
          missed: 0,
          cancelled: 0,
        };
        inspections.forEach((t) => {
          breakdown[normalizeStatus(t.status)] += 1;
        });

        const total = inspections.length;
        const completionRate = total
          ? Math.round((breakdown.completed / total) * 100)
          : 0;

        // Only treat an explicitly-present, parseable value as a real date.
        // moment(undefined) returns "now" (and is .isValid()), which would push
        // date-less inspections to the top of the list and show today.
        const inspMoment = (t) => {
          const raw = t.completedAt || t.createdAt;
          if (!raw) return null;
          const m = moment(raw);
          return m.isValid() ? m : null;
        };

        const recentInspections = [...inspections]
          .sort((a, b) => {
            const ma = inspMoment(a);
            const mb = inspMoment(b);
            return (mb ? mb.valueOf() : 0) - (ma ? ma.valueOf() : 0);
          })
          .slice(0, 5)
          .map((t) => {
            const m = inspMoment(t);
            return {
              id: t._id,
              name: t.propertyName || "Untitled inspection",
              status: normalizeStatus(t.status),
              assignee: inspectorIds(t).length
                ? inspectorIds(t)
                    .map((u) => userLabel[u] || "Unknown inspector")
                    .join(", ")
                : "Unassigned",
              date: m ? m.format("MMM D, YYYY") : "—",
            };
          });

        // --- FR-13: per-inspector performance -----------------------------
        // Group by each assigned inspector (any one may complete an inspection,
        // so it counts toward each). Empty -> "Unassigned" bucket.
        const inspectorBuckets = {};
        inspections.forEach((t) => {
          const ids = inspectorIds(t);
          const keys = ids.length ? ids : ["__unassigned__"];
          const s = normalizeStatus(t.status);
          keys.forEach((uid) => {
            if (!inspectorBuckets[uid]) {
              inspectorBuckets[uid] = { total: 0, completed: 0, pending: 0 };
            }
            const b = inspectorBuckets[uid];
            b.total += 1;
            if (s === "completed") b.completed += 1;
            else b.pending += 1; // pending + in progress + missed = "open"
          });
        });

        const inspectorPerformance = Object.keys(inspectorBuckets)
          .map((uid) => {
            const b = inspectorBuckets[uid];
            const name =
              uid === "__unassigned__"
                ? "Unassigned"
                : userLabel[uid] || "Unknown inspector";
            return {
              name,
              total: b.total,
              completed: b.completed,
              pending: b.pending,
              completionRate: b.total
                ? Math.round((b.completed / b.total) * 100)
                : 0,
            };
          })
          .sort((a, b) => b.total - a.total || b.completed - a.completed);

        // --- FR-13: flagged-issue analytics -------------------------------
        // A flagged issue = a check answered "No" (checks[].answer).
        // Defensive: questionaire and checks may be missing/non-arrays.
        let totalFlagged = 0;
        let inspectionsWithIssues = 0;
        const issuesByProperty = {};
        inspections.forEach((t) => {
          const questionaire = Array.isArray(t.questionaire)
            ? t.questionaire
            : [];
          let flaggedHere = 0;
          questionaire.forEach((q) => {
            const checks = q && Array.isArray(q.checks) ? q.checks : [];
            checks.forEach((p) => {
              if (preQIsNo(p)) flaggedHere += 1;
            });
          });
          if (flaggedHere > 0) {
            totalFlagged += flaggedHere;
            inspectionsWithIssues += 1;
            const propName = t.propertyName || "Untitled property";
            issuesByProperty[propName] =
              (issuesByProperty[propName] || 0) + flaggedHere;
          }
        });

        const byProperty = Object.keys(issuesByProperty)
          .map((propertyName) => ({
            propertyName,
            count: issuesByProperty[propertyName],
          }))
          .sort((a, b) => b.count - a.count);

        const flaggedIssues = {
          totalFlagged,
          inspectionsWithIssues,
          byProperty,
        };

        // --- FR-13: 8-week trends (completed inspections + flagged issues) ---
        // Bucket completed inspections by ISO week (Mon start) from their
        // completion date, plus the flagged-problem count found in each.
        const WEEKS = 8;
        const thisWeekStart = moment().startOf("isoWeek");
        const weeklyTrend = [];
        const weekIndex = {};
        for (let i = WEEKS - 1; i >= 0; i--) {
          const wStart = thisWeekStart.clone().subtract(i, "weeks");
          weekIndex[wStart.format("YYYY-MM-DD")] = weeklyTrend.length;
          weeklyTrend.push({
            label: wStart.format("MMM D"),
            completed: 0,
            flagged: 0,
          });
        }
        inspections.forEach((t) => {
          if (normalizeStatus(t.status) !== "completed") return;
          const raw = t.completedAt || t.lastInspectionDate || t.createdAt;
          if (!raw) return;
          const m = moment(raw);
          if (!m.isValid()) return;
          const idx = weekIndex[m.startOf("isoWeek").format("YYYY-MM-DD")];
          if (idx === undefined) return; // older than the 8-week window
          weeklyTrend[idx].completed += 1;
          const questionaire = Array.isArray(t.questionaire) ? t.questionaire : [];
          questionaire.forEach((q) => {
            const checks = q && Array.isArray(q.checks) ? q.checks : [];
            checks.forEach((p) => {
              if (preQIsNo(p)) weeklyTrend[idx].flagged += 1;
            });
          });
        });

        if (!active) return;
        setData({
          totalUsers: usersSnap.size,
          totalProperties: propertiesSnap.size,
          totalInspections: total,
          openInspections: breakdown.pending + breakdown["in progress"],
          completionRate,
          statusBreakdown: breakdown,
          recentInspections,
          inspectorPerformance,
          flaggedIssues,
          weeklyTrend,
          openQuestions,
        });
      } catch (err) {
        console.error("Dashboard data load failed:", err);
        if (!active) return;
        setData(EMPTY);
        setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  return { ...data, loading, error };
};

export default useDashboardData;
